import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ExternalLink } from "lucide-react";
import { formatRelativeTime, truncate } from "@/lib/utils";
import type { UrlSummary } from "@/types/validation";

interface UrlSummaryTableProps {
  urls: UrlSummary[];
  isLoading?: boolean;
  onViewUrl: (url: string) => void;
  onScrapeUrl: (url: string) => void;
}

export function UrlSummaryTable({
  urls,
  isLoading,
  onViewUrl,
  onScrapeUrl,
}: UrlSummaryTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-16 bg-muted rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (urls.length === 0) {
    return (
      <div className="text-center py-12 border border-border rounded-lg">
        <p className="text-muted-foreground">
          No URLs found with validation issues
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          Try adjusting your filters
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>URL</TableHead>
            <TableHead className="w-24">Source</TableHead>
            <TableHead className="w-24 text-center">Total</TableHead>
            <TableHead className="w-24 text-center">Errors</TableHead>
            <TableHead className="w-24 text-center">Warnings</TableHead>
            <TableHead className="w-24 text-center">Unresolved</TableHead>
            <TableHead className="w-32">Last Scraped</TableHead>
            <TableHead className="w-48 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {urls.map((urlSummary) => (
            <TableRow key={urlSummary.url}>
              <TableCell>
                <a
                  href={urlSummary.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:opacity-90 text-sm flex items-center gap-1"
                >
                  {truncate(urlSummary.url, 60)}
                  <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </a>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="uppercase text-xs">
                  {urlSummary.source}
                </Badge>
              </TableCell>
              <TableCell className="text-center">
                <span className="font-medium">{urlSummary.total_issues}</span>
              </TableCell>
              <TableCell className="text-center">
                <span className="text-destructive font-medium">
                  {urlSummary.error_count}
                </span>
              </TableCell>
              <TableCell className="text-center">
                <span className="text-warning font-medium">
                  {urlSummary.warning_count}
                </span>
              </TableCell>
              <TableCell className="text-center">
                <span className="font-medium">
                  {urlSummary.unresolved_count}
                </span>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatRelativeTime(urlSummary.last_scraped_at)}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onViewUrl(urlSummary.url)}
                  >
                    View Issues
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onScrapeUrl(urlSummary.url)}
                  >
                    Scrape Again
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
